import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import './contact.css';

// Web3Forms form access keys are public identifiers, not private API credentials.
const ACCESS_KEY = 'a753e8ea-62bd-4722-9b2a-a2c8fab3acaa';
const ENDPOINT = 'https://api.web3forms.com/submit';

export function focusContact() {
  document.getElementById('contact-heading')?.focus({ preventScroll: true });
}

const ContactSection = ({ product }) => {
  const [status, setStatus] = useState('idle');
  const request = useRef(null);
  const mounted = useRef(true);
  const statusRef = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      request.current?.abort();
    };
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();
    if (request.current) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    // This hidden trap supplements Web3Forms' server-side spam filtering.
    if (data.get('botcheck')) return;
    const message = String(data.get('message') || '').trim();
    if (!message) {
      form.elements.message.setCustomValidity('Please enter your message.');
      form.elements.message.reportValidity();
      return;
    }
    data.set('message', message);
    data.set('email', String(data.get('email')).trim());
    data.set('name', String(data.get('name') || '').trim());
    data.set('access_key', ACCESS_KEY);
    data.set('from_name', 'Logic Gates website');
    data.set('subject', product ? `Website enquiry: ${product.name}` : 'Website enquiry: Logic Gates');
    data.set('page', product ? `https://logicgat.es/product/${product.id}` : 'https://logicgat.es/');
    if (product) data.set('board', product.name);

    const controller = new AbortController();
    request.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    setStatus('sending');
    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      const result = await response.json();
      if (!response.ok || result.success !== true) throw new Error('Submission not accepted');
      if (mounted.current) {
        form.reset();
        setStatus('success');
      }
    } catch {
      // Keep the visitor's message and avoid automatic retries: a lost response
      // can occur after delivery, so blindly retrying could send duplicates.
      if (mounted.current) setStatus('error');
    } finally {
      window.clearTimeout(timeout);
      request.current = null;
    }
  }

  useEffect(() => {
    if (status === 'success' || status === 'error') statusRef.current?.focus({ preventScroll: true });
  }, [status]);

  return (
    <section id="contact" className="contact-section" aria-labelledby="contact-heading">
      <div className="contact-layout">
        <div className="contact-intro">
          <p className="pcb-eyebrow">Talk to the maker</p>
          <h2 id="contact-heading" tabIndex={-1}>A board for your desk.<br />A set for your classroom.</h2>
          <p>Planning a bulk order, equipping a lab, or have a question about a board? Send me a message.</p>
          <p className="contact-bulk-hint">For bulk or classroom orders, include the board, approximate quantity and delivery country so I can help.</p>
          <p className="contact-signature">Oliver <span>Designer &amp; maker · Estonia</span></p>
        </div>

        <form className="contact-form" onSubmit={handleSubmit} aria-label="Contact Oliver"
          onChange={(event) => {
            event.target.setCustomValidity?.('');
            if (status === 'success' || status === 'error') setStatus('idle');
          }}>
          {product && <p className="contact-board">About <strong>{product.name}</strong></p>}
          <fieldset disabled={status === 'sending'}>
            <legend className="sr-only">Your contact details and message</legend>
            <div className="contact-fields">
              <div className="contact-field">
                <label htmlFor="contact-name">Name <span>(optional)</span></label>
                <input id="contact-name" name="name" type="text" autoComplete="name" maxLength={100} />
              </div>
              <div className="contact-field">
                <label htmlFor="contact-email">Your email</label>
                <input id="contact-email" name="email" type="email" autoComplete="email" required maxLength={254} aria-describedby="contact-email-hint" />
                <p id="contact-email-hint" className="contact-hint">The address I should reply to.</p>
              </div>
            </div>
            <div className="contact-field">
              <label htmlFor="contact-message">Message</label>
              <textarea id="contact-message" name="message" rows={5} required maxLength={5000}
                placeholder={product ? `What would you like to know about ${product.name}?` : 'How can I help?'} />
            </div>
            <input type="checkbox" name="botcheck" className="contact-trap" tabIndex={-1} aria-hidden="true" />
            <button type="submit" className="pcb-btn pcb-btn-primary contact-submit" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending…' : 'Send message'}
              <ArrowRight size={17} aria-hidden="true" />
            </button>
          </fieldset>
          <div ref={statusRef} className={`contact-status contact-status-${status}`}
            role={status === 'error' ? 'alert' : 'status'} aria-live={status === 'error' ? 'assertive' : 'polite'}
            aria-atomic="true" tabIndex={-1}>
            {status === 'sending' && <p>Sending your message…</p>}
            {status === 'success' && <p><CheckCircle2 size={20} aria-hidden="true" /><span><strong>Thanks for getting in touch.</strong> Your message was sent. I’ll reply to the email address you provided.</span></p>}
            {status === 'error' && <p>We couldn’t confirm your message was sent. Your text is still here. Please try again, or email <a href="mailto:oliver.vaht@gmail.com">oliver.vaht@gmail.com</a>.</p>}
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
